import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateMenuCollection,
  useMenuCollections,
  useUpdateMenuCollection,
} from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";
import type { UpsertMenuCollectionInput } from "@/types/menu";

const createEmptyFormState = (): UpsertMenuCollectionInput => ({
  name: "",
  slug: "",
  description: "",
  active: true,
});

export default function CollectionFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data: collectionsData } = useMenuCollections(activeOrganization?.id);
  const createCollection = useCreateMenuCollection();
  const updateCollection = useUpdateMenuCollection();
  const [formState, setFormState] = useState<UpsertMenuCollectionInput>(createEmptyFormState());

  const editingCollection = id ? collectionsData?.collections.find((c) => c.id === id) : undefined;

  useEffect(() => {
    if (editingCollection) {
      setFormState({
        name: editingCollection.name,
        slug: editingCollection.slug,
        description: editingCollection.description ?? "",
        active: editingCollection.active,
        displayOrder: editingCollection.displayOrder,
      });
    }
  }, [editingCollection]);

  const handleChange = <T extends keyof UpsertMenuCollectionInput>(
    field: T,
    value: UpsertMenuCollectionInput[T],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload: UpsertMenuCollectionInput = {
      name: formState.name?.trim() ?? "",
      slug: formState.slug?.trim() || undefined,
      description: formState.description?.trim() || null,
      active: formState.active ?? true,
    };

    if (!payload.name) {
      toast.error(t("menu.collections.notifications.nameRequired"));
      return;
    }

    if (id) {
      updateCollection.mutate(
        { data: payload, id },
        {
          onError: (mutationError) => {
            toast.error(mutationError.message);
          },
          onSuccess: () => {
            toast.success(t("menu.collections.notifications.updated"));
            navigate("/menu/collections");
          },
        },
      );
      return;
    }

    createCollection.mutate(payload, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.collections.notifications.created"));
        navigate("/menu/collections");
      },
    });
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.collections.title")}</CardTitle>
            <CardDescription>
              {t("menu.collections.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const formTitle = id
    ? t("menu.collections.form.editTitle")
    : t("menu.collections.form.createTitle");

  const submitLabel = id ? t("menu.collections.form.save") : t("menu.collections.form.create");

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">{formTitle}</h1>
        <p className="text-muted-foreground text-sm">{t("menu.collections.form.description")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{formTitle}</CardTitle>
          <CardDescription>{t("menu.collections.form.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="menu-collection-name">{t("menu.collections.form.name")}</Label>
              <Input
                id="menu-collection-name"
                onChange={(event) => handleChange("name", event.target.value)}
                placeholder={t("menu.collections.form.namePlaceholder")}
                value={formState.name ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-collection-slug">{t("menu.collections.form.slug")}</Label>
              <Input
                id="menu-collection-slug"
                onChange={(event) => handleChange("slug", event.target.value)}
                placeholder={t("menu.collections.form.slugPlaceholder")}
                value={formState.slug ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="menu-collection-description">
                {t("menu.collections.form.longDescription")}
              </Label>
              <Textarea
                id="menu-collection-description"
                onChange={(event) => handleChange("description", event.target.value)}
                placeholder={t("menu.collections.form.descriptionPlaceholder")}
                rows={4}
                value={formState.description ?? ""}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">{t("menu.collections.form.active")}</p>
                <p className="text-muted-foreground text-sm">
                  {t("menu.collections.form.activeDescription")}
                </p>
              </div>
              <Switch
                checked={formState.active ?? true}
                onCheckedChange={(checked) => handleChange("active", checked)}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                disabled={createCollection.isPending || updateCollection.isPending}
                type="submit"
              >
                {submitLabel}
              </Button>
              <Button onClick={() => navigate("/menu/collections")} type="button" variant="outline">
                {t("menu.collections.form.cancel")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
